import { message } from 'antd';
import { useIntl } from 'react-intl';
import { Button, GoogleMap, Modal } from 'ui';
import { Icon } from 'ui/Icon';
import { LatLng } from 'utils/geoHelpers';
import {
  DAY_KEYS,
  OpenHoursDays,
  getOpenStatus,
  hasAnyOpenHours,
  openingOf,
  todayIndex,
} from '../utils/openStatus';
import { StatusPill } from './FarmSection';

const MAP_ZOOM = 15;
const MODAL_Z_INDEX = 1020;

export interface FarmPlaceDetails {
  key: string;
  title: string;
  subtitle?: string;
  tag?: string | null;
  tagIcon: string;
  point: LatLng | null;
  address?: string;
  phone?: string | null;
  email?: string | null;
  days: OpenHoursDays;
  note?: string | null;
}

interface FarmPlaceModalProps {
  place: FarmPlaceDetails | null;
  onClose: () => void;
}

const telHref = (phone: string) => `tel:${phone.replace(/[\s()-]/g, '')}`;

interface InfoIconProps {
  name: string;
  smallName?: string;
}

const InfoIcon = ({ name, smallName = `${name}-sm` }: InfoIconProps) => (
  <span className={'farm-modal-info-icon'}>
    <span className={'farm-modal-info-icon-lg'}>
      <Icon name={name} type={'any'} />
    </span>
    <span className={'farm-modal-info-icon-sm'}>
      <Icon name={smallName} type={'any'} />
    </span>
  </span>
);

const FarmPlaceModal = ({ place, onClose }: FarmPlaceModalProps) => {
  const intl = useIntl();

  if (!place) return null;

  const status = getOpenStatus(place.days);
  const currentDay = todayIndex();
  const destination = place.point
    ? `${place.point.lat},${place.point.lng}`
    : (place.address ?? '');
  const googleUrl = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(destination)}`;
  const wazeUrl = place.point
    ? `https://waze.com/ul?ll=${encodeURIComponent(destination)}&navigate=yes`
    : `https://waze.com/ul?navigate=yes&q=${encodeURIComponent(destination)}`;

  const copyAddress = async () => {
    if (!place.address) return;

    try {
      await navigator.clipboard.writeText(place.address);
      message.success(intl.formatMessage({ id: 'farm_view.address_copied' }));
    } catch {
      message.error(intl.formatMessage({ id: 'validation.error' }));
    }
  };

  return (
    <Modal
      open
      onCancel={onClose}
      footer={null}
      closable={false}
      centered
      width={820}
      wrapClassName={'farm-place-modal'}
      zIndex={MODAL_Z_INDEX}
      destroyOnClose
    >
      <div className={`farm-modal${place.point ? '' : ' no-media'}`}>
        <span className={'farm-modal-handle'} />
        {place.point && (
          <div className={'farm-modal-media'}>
            <div className={'farm-modal-map'}>
              <GoogleMap
                features={[{ id: 1, position: place.point }]}
                center={place.point}
                zoom={MAP_ZOOM}
              />
            </div>
            {!!place.tag && (
              <span className={'farm-modal-tag'}>
                <span className={'farm-modal-tag-icon'}>
                  <Icon name={place.tagIcon} type={'any'} />
                </span>
                {place.tag}
              </span>
            )}
          </div>
        )}
        <Button
          className={'farm-modal-close'}
          title={intl.formatMessage({ id: 'general.close' })}
          icon={<Icon name={'farm-modal-close'} type={'any'} />}
          onClick={onClose}
        />

        <div className={'farm-modal-body'}>
          <div className={'farm-modal-heading'}>
            <div className={'farm-modal-heading-text'}>
              <h3 className={'farm-modal-title'}>{place.title}</h3>
              {!!place.subtitle && (
                <p className={'farm-modal-subtitle'}>{place.subtitle}</p>
              )}
            </div>
            {status && <StatusPill status={status} />}
          </div>

          <div className={'farm-modal-columns'}>
            <div className={'farm-modal-info'}>
              {!!place.address && (
                <div className={'farm-modal-info-row'}>
                  <InfoIcon name={'farm-modal-address'} />
                  <span className={'farm-modal-info-text'}>
                    <span className={'farm-modal-info-label'}>
                      {intl.formatMessage({ id: 'farm_view.info_address' })}
                    </span>
                    <span className={'farm-modal-info-value'}>
                      {place.address}
                    </span>
                  </span>
                  <Button
                    className={'farm-modal-copy'}
                    title={intl.formatMessage({
                      id: 'farm_view.copy_address',
                    })}
                    icon={<Icon name={'farm-modal-copy'} type={'any'} />}
                    onClick={copyAddress}
                  />
                </div>
              )}
              {!!place.phone && (
                <div className={'farm-modal-info-row'}>
                  <InfoIcon name={'farm-modal-phone'} />
                  <span className={'farm-modal-info-text'}>
                    <span className={'farm-modal-info-label'}>
                      {intl.formatMessage({ id: 'farm_view.info_phone' })}
                    </span>
                    <a
                      className={'farm-modal-info-value'}
                      href={telHref(place.phone)}
                    >
                      {place.phone}
                    </a>
                  </span>
                </div>
              )}
              {!!place.email && (
                <div className={'farm-modal-info-row'}>
                  <InfoIcon
                    name={'farm-modal-email'}
                    smallName={'farm-modal-email'}
                  />
                  <span className={'farm-modal-info-text'}>
                    <span className={'farm-modal-info-label'}>
                      {intl.formatMessage({ id: 'farm_view.info_email' })}
                    </span>
                    <a
                      className={'farm-modal-info-value'}
                      href={`mailto:${place.email}`}
                    >
                      {place.email}
                    </a>
                  </span>
                </div>
              )}
              {!!destination && (
                <div className={'farm-modal-nav'}>
                  <a href={googleUrl} target={'_blank'} rel={'noreferrer'}>
                    <span className={'farm-modal-nav-icon'}>
                      <Icon name={'farm-modal-nav'} type={'any'} />
                    </span>
                    {intl.formatMessage({ id: 'farms.google_map' })}
                  </a>
                  <a href={wazeUrl} target={'_blank'} rel={'noreferrer'}>
                    <span className={'farm-modal-nav-icon'}>
                      <Icon name={'farm-modal-nav'} type={'any'} />
                    </span>
                    {intl.formatMessage({ id: 'farms.waze' })}
                  </a>
                </div>
              )}
            </div>

            {hasAnyOpenHours(place.days) && (
              <div className={'farm-modal-hours'}>
                <div className={'farm-modal-hours-head'}>
                  <InfoIcon name={'farm-modal-clock'} />
                  {intl.formatMessage({ id: 'farm_view.hours_title' })}
                </div>
                {DAY_KEYS.map((dayKey, index) => {
                  const opening = openingOf(place.days[index]);
                  const isToday = index === currentDay;

                  return (
                    <div
                      key={dayKey}
                      className={`farm-modal-hours-row${isToday ? ' today' : ''}`}
                    >
                      <span className={'farm-modal-hours-day'}>
                        {intl.formatMessage({ id: `open_hours.${dayKey}` })}
                      </span>
                      {isToday && (
                        <span className={'farm-modal-hours-today'}>
                          {intl.formatMessage({ id: 'farm_view.today' })}
                        </span>
                      )}
                      <span
                        className={`farm-modal-hours-time${opening ? '' : ' closed'}`}
                      >
                        {opening
                          ? `${opening.from} – ${opening.to}`
                          : intl.formatMessage({ id: 'open_hours.closed' })}
                      </span>
                    </div>
                  );
                })}
                <p className={'farm-modal-hours-note'}>
                  {[
                    place.note?.trim(),
                    intl.formatMessage({ id: 'farm_view.hours_note' }),
                  ]
                    .filter(Boolean)
                    .join(' ')}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default FarmPlaceModal;
